"use client";

import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, ChevronDown, Globe } from "lucide-react";
import { ThemeToggle } from "@/components/theme-toggle";
import { cn } from "@/lib/utils";
import { getTranslation } from "@/lib/translations";
import { defaultLocale, getLocaleFromPathname, getPathnameWithoutLocale, type Locale } from "@/lib/i18n";
import { motion, AnimatePresence } from "framer-motion";
import { EmailLink } from "@/components/email-link";

interface NavigationProps {
  locale?: Locale;
}

export function Navigation({ locale = defaultLocale }: NavigationProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const currentLocale = getLocaleFromPathname(location.pathname);
  const t = getTranslation(currentLocale);

  // Pages without a hero image — navigation must always show the light/scrolled style
  const pathWithoutLocale = getPathnameWithoutLocale(location.pathname).replace(/^\//, '');
  const noHeroPaths = [
    t.routes.contact,
    `${t.routes.careers}/${t.routes.jobOpenings}`,
    t.routes.news,
    t.routes.documents,
    'privacy',
    'imprint',
    `${t.routes.about}/${t.routes.anfahrt}`,
    `${t.routes.services}/${t.routes.schaltschrankbau}`,
    `${t.routes.services}/${t.routes.engineering}`,
    `${t.routes.services}/${t.routes.softwareengineering}`,
    `${t.routes.products}/${t.routes.athex}`,
    `${t.routes.products}/${t.routes.panelscout}`,
    'kundenportal',
  ];
  const decodedPath = (() => {
    try {
      return decodeURIComponent(pathWithoutLocale);
    } catch {
      return pathWithoutLocale;
    }
  })();
  const normalizedPath = decodedPath.toLowerCase();
  const forceLightBg = noHeroPaths.some(
    (p) => normalizedPath === p.toLowerCase() || normalizedPath.startsWith(`${p.toLowerCase()}/`)
  );
  const effectiveScrolled = isScrolled || forceLightBg;

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDelta = currentScrollY - lastScrollY;

      setIsScrolled(currentScrollY >= 20);

      if (currentScrollY <= 0) {
        setIsVisible(true);
      } else if (scrollDelta > 0) {
        setIsVisible(false);
      } else if (scrollDelta < 0) {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setIsLanguageOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getLocalizedPath = (path: string, targetLocale: Locale) => {
    if (targetLocale === defaultLocale) {
      return path;
    }
    return `/${targetLocale}${path}`;
  };

  const getCurrentPathForLocale = (targetLocale: Locale) => {
    const currentPath = location.pathname;
    const currentPathWithoutLocale = getPathnameWithoutLocale(currentPath);
    
    const pathSegments = currentPathWithoutLocale.split('/').filter(Boolean);
    
    if (pathSegments.length === 0) {
      return targetLocale === defaultLocale ? '/' : `/${targetLocale}`;
    }
    
    if (pathSegments.length === 2) {
      const [parentSegment, childSegment] = pathSegments;
      
      const currentT = getTranslation(currentLocale);
      const targetT = getTranslation(targetLocale);
      
      let parentKey = '';
      let childKey = '';
      
      for (const [key, route] of Object.entries(currentT.routes)) {
        if (route === parentSegment) {
          parentKey = key;
          break;
        }
      }
      
      for (const [key, route] of Object.entries(currentT.routes)) {
        if (route === childSegment) {
          childKey = key;
          break;
        }
      }
      
      if (parentKey && childKey) {
        const newParentRoute = targetT.routes[parentKey as keyof typeof targetT.routes] || parentSegment;
        const newChildRoute = targetT.routes[childKey as keyof typeof targetT.routes] || childSegment;
        const newPath = `/${newParentRoute}/${newChildRoute}`;
        return targetLocale === defaultLocale ? newPath : `/${targetLocale}${newPath}`;
      }
    }
    
    if (pathSegments.length === 1) {
      const segment = pathSegments[0];
      const currentT = getTranslation(currentLocale);
      const targetT = getTranslation(targetLocale);
      
      for (const [key, route] of Object.entries(currentT.routes)) {
        if (route === segment) {
          const newRoute = targetT.routes[key as keyof typeof targetT.routes] || segment;
          const newPath = `/${newRoute}`;
          return targetLocale === defaultLocale ? newPath : `/${targetLocale}${newPath}`;
        }
      }
    }
    
    return getLocalizedPath(currentPathWithoutLocale, targetLocale);
  };

  const navItems = [
    { 
      key: "services", 
      href: `/${t.routes.services}`,
      dropdown: [
        { key: "schaltschrankbau", href: `/${t.routes.services}/${t.routes.schaltschrankbau}`, label: t.services.schaltschrankbau.title },
        { key: "engineering", href: `/${t.routes.services}/${t.routes.engineering}`, label: t.services.engineering.title },
        { key: "softwareengineering", href: `/${t.routes.services}/${t.routes.softwareengineering}`, label: t.services.softwareengineering.title }
      ]
    },
    // Products hidden from nav but pages still exist

    { 
      key: "about",
      href: `/${t.routes.about}`,
      dropdown: [
        { key: "leitbild", href: `/${t.routes.about}/${t.routes.leitbild}`, label: t.nav.leitbild },
        { key: "team", href: `/${t.routes.about}/${t.routes.team}`, label: t.nav.team },
        { key: "partners", href: `/${t.routes.about}/${t.routes.partners}`, label: t.nav.partners },
        { key: "sustainability", href: `/${t.routes.sustainability}`, label: t.nav.sustainability },
        { key: "chameleonStory", href: `/${t.routes.about}/${t.routes.chameleonStory}`, label: t.nav.chameleonStory },
        { key: "anfahrt", href: `/${t.routes.about}/${t.routes.anfahrt}`, label: t.nav.anfahrt }
      ]
    },
    { 
      key: "careers", 
      href: `/${t.routes.careers}`,
      dropdown: [
        { key: "jobOpenings", href: `/${t.routes.careers}/${t.routes.jobOpenings}`, label: t.careers.openings.title },
        { key: "vocationalTraining", href: `/${t.routes.careers}/${t.routes.vocationalTraining}`, label: t.nav.vocationalTraining }
      ]
    },
    { key: "news", href: `/${t.routes.news}` },
    { key: "contact", href: `/${t.routes.contact}` },
  ];

  const locales = [
    { code: "de", label: "Deutsch" },
    { code: "en", label: "English" },
    { code: "fr", label: "Français" },
    { code: "it", label: "Italiano" },
  ];

  const handleLanguageChange = (newLocale: Locale) => {
    const newPath = getCurrentPathForLocale(newLocale);
    navigate(newPath, { state: { isLanguageChange: true } });
  };

  const toggleExpanded = (key: string) => {
    setExpandedItems(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  const handleNavigate = (href: string) => {
    navigate(getLocalizedPath(href, currentLocale));
    setIsMenuOpen(false);
    setExpandedItems([]);
  };

  return (
    <>
      {/* Main Navigation Bar */}
      <nav
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300 py-3 lg:py-4 group/nav",
          "bg-transparent lg:hover:bg-white/70 lg:dark:hover:bg-background/70 lg:hover:backdrop-blur-md",
          effectiveScrolled && "bg-white/70 dark:bg-background/70 backdrop-blur-md",
          (isVisible || isMenuOpen) ? "translate-y-0" : "-translate-y-full"
        )}
      >
        <div className="container mx-auto px-6 lg:px-8">
          <div className="flex items-center h-12">
            {/* Logo Left */}
            <button
              type="button"
              className="flex items-center transition-all duration-300 hover:opacity-80 cursor-pointer select-none focus:outline-none focus-visible:outline-none bg-transparent border-0 p-0"
              style={{ WebkitTapHighlightColor: "transparent", WebkitUserSelect: "none", userSelect: "none" }}
              onMouseDown={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              onClick={() => {
                setIsMenuOpen(false);
                setExpandedItems([]);
                setIsLanguageOpen(false);
                navigate(getLocalizedPath("/", currentLocale));
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              <img 
                src="/lovable-uploads/althaus-logo-new.png" 
                alt="Althaus AG - Leading in automation" 
                className="h-12 lg:h-14 w-auto pointer-events-none select-none"
                style={{ WebkitUserSelect: "none", userSelect: "none", WebkitUserDrag: "none" } as React.CSSProperties}
                draggable={false}
              />
            </button>

            {/* Desktop: Horizontal Menu Items - Centered */}
            <div className="hidden lg:flex items-center justify-center flex-1 gap-6 xl:gap-8 h-full">
              {navItems.map((item) => (
                <div key={item.key} className="relative group flex items-center h-full">
                  {item.dropdown ? (
                    <>
                      <Link
                        to={getLocalizedPath(item.href, currentLocale)}
                        className={cn(
                          "text-[13px] font-medium tracking-[0.1em] uppercase transition-colors duration-200 py-2 border-b-2 border-transparent",
                          "text-white/95 lg:group-hover/nav:text-gray-800 hover:border-white/60 lg:group-hover/nav:hover:border-gray-400",
                          "dark:text-white/90 dark:lg:group-hover/nav:text-white dark:hover:text-white dark:hover:border-white/60",
                          effectiveScrolled && "text-gray-800 hover:border-gray-400 dark:text-white/90 dark:hover:border-white/60"
                        )}
                      >
                        {t.nav[item.key as keyof typeof t.nav]}
                      </Link>
                      {/* Dropdown on hover */}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                        <div className="bg-white dark:bg-gray-900 py-3 px-4 min-w-[200px] rounded-lg shadow-2xl">
                          {/* Übersicht link */}
                          <Link
                            to={getLocalizedPath(item.href, currentLocale)}
                            className="block py-2 text-center text-gray-700 dark:text-white/70 hover:text-gray-900 dark:hover:text-white text-[13px] tracking-wide transition-colors font-medium"
                          >
                            {t.nav.overview}
                          </Link>
                          <div className="border-b border-gray-300/50 dark:border-white/10 my-1.5" />
                          {item.dropdown.map((subItem) => (
                            <Link
                              key={subItem.key}
                              to={getLocalizedPath(subItem.href, currentLocale)}
                              className="block py-2 text-center text-gray-700 dark:text-white/70 hover:text-gray-900 dark:hover:text-white text-[13px] tracking-wide transition-colors"
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <Link
                      to={getLocalizedPath(item.href, currentLocale)}
                        className={cn(
                          "text-[13px] font-medium tracking-[0.1em] uppercase transition-colors duration-200 py-2 border-b-2 border-transparent",
                          "text-white/95 lg:group-hover/nav:text-gray-800 hover:border-white/60 lg:group-hover/nav:hover:border-gray-400",
                          "dark:text-white/90 dark:lg:group-hover/nav:text-white dark:hover:text-white dark:hover:border-white/60",
                          effectiveScrolled && "text-gray-800 hover:border-gray-400 dark:text-white/90 dark:hover:border-white/60"
                        )}
                    >
                      {t.nav[item.key as keyof typeof t.nav]}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Language & Theme - Right aligned */}
            <div className={cn(
              "hidden lg:flex items-center border-l pl-6 transition-colors duration-200",
              "border-white/40 lg:group-hover/nav:border-gray-600 dark:border-white/20",
              effectiveScrolled && "border-gray-600"
            )}>
              {/* Language Selector Dropdown */}
              <div ref={languageDropdownRef} className="relative">
                <button
                  onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                  className={cn(
                    "flex items-center transition-colors duration-200 p-2 rounded-lg",
                    "text-white/95 lg:group-hover/nav:text-gray-800 hover:bg-white/10 lg:group-hover/nav:hover:bg-gray-100",
                    "dark:text-white/90 dark:lg:group-hover/nav:text-white dark:hover:text-white dark:hover:bg-white/10 dark:lg:group-hover/nav:hover:bg-white/10",
                    effectiveScrolled && "text-gray-800 hover:bg-gray-100 dark:text-white/90 dark:hover:bg-white/10"
                  )}
                  aria-label="Sprache wählen"
                >
                  <Globe className="h-5 w-5" />
                </button>
                
                {/* Dropdown Menu */}
                <AnimatePresence>
                  {isLanguageOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full right-0 mt-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-lg py-2 z-50"
                    >
                      {locales.map((loc) => (
                        <button
                          key={loc.code}
                          onClick={() => {
                            handleLanguageChange(loc.code as Locale);
                            setIsLanguageOpen(false);
                          }}
                          className={cn(
                            "block w-full text-center px-3 py-2 text-[13px] tracking-wide uppercase transition-all duration-300",
                            currentLocale === loc.code
                              ? "text-gray-900 dark:text-white border-l-2 border-primary dark:border-white/60 bg-gray-100 dark:bg-white/5" 
                              : "text-gray-600 dark:text-white/60 hover:text-gray-900 dark:hover:text-white border-l-2 border-transparent hover:border-gray-400 dark:hover:border-white/40 hover:bg-gray-100 dark:hover:bg-white/5"
                          )}
                        >
                          {loc.code.toUpperCase()}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Theme Toggle */}
              <div className="ml-0">
                <ThemeToggle />
              </div>
            </div>

            {/* Mobile: Hamburger Button */}
            <div className="lg:hidden ml-auto">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex flex-col justify-center items-center w-10 h-10 gap-1.5 group bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent focus:outline-none focus-visible:outline-none [-webkit-tap-highlight-color:transparent] transition-all duration-300 rounded"
                aria-label="Toggle menu"
              >
                <motion.span 
                  className={cn("w-6 h-0.5 transition-colors duration-200 dark:bg-white", effectiveScrolled || isMenuOpen ? "bg-gray-800" : "bg-white")}
                  animate={isMenuOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                />
                <motion.span 
                  className={cn("w-6 h-0.5 transition-colors duration-200 dark:bg-white", effectiveScrolled || isMenuOpen ? "bg-gray-800" : "bg-white")}
                  animate={isMenuOpen ? { opacity: 0 } : { opacity: 1 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                />
                <motion.span 
                  className={cn("w-6 h-0.5 transition-colors duration-200 dark:bg-white", effectiveScrolled || isMenuOpen ? "bg-gray-800" : "bg-white")}
                  animate={isMenuOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Fullscreen Overlay Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-white/95 dark:bg-background/95 backdrop-blur-md overflow-y-auto scrollbar-hide lg:hidden"
          >
            {/* Menu Content */}
            <div className="container mx-auto px-4 pt-32 pb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="flex flex-col space-y-2"
              >
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.key}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
                  >
                    {item.dropdown ? (
                      <div className="border-b border-gray-300/50 dark:border-white/10">
                        {/* Parent item with expand toggle */}
                        <button
                          onClick={() => toggleExpanded(item.key)}
                          className="w-full flex items-center justify-between py-4 text-gray-900 dark:text-white hover:text-gray-600 dark:hover:text-white/80 transition-colors group"
                        >
                          <span className="text-2xl font-light tracking-wide">
                            {t.nav[item.key as keyof typeof t.nav]}
                          </span>
                          <ChevronRight 
                            className={cn(
                              "h-6 w-6 transition-transform duration-300",
                              expandedItems.includes(item.key) && "rotate-90"
                            )}
                          />
                        </button>
                        
                        {/* Dropdown items */}
                        <AnimatePresence>
                          {expandedItems.includes(item.key) && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="pl-6 pb-4 space-y-2">
                                {/* Main category link */}
                                <button
                                  onClick={() => handleNavigate(item.href)}
                                  className="block text-lg text-gray-600 dark:text-white/70 hover:text-gray-900 dark:hover:text-white transition-colors py-2"
                                >
                                  {t.nav.overview}
                                </button>
                                {/* Sub items */}
                                {item.dropdown.map((subItem) => (
                                  <button
                                    key={subItem.key}
                                    onClick={() => handleNavigate(subItem.href)}
                                    className="block text-lg text-gray-600 dark:text-white/70 hover:text-gray-900 dark:hover:text-white transition-colors py-2"
                                  >
                                    {subItem.label}
                                  </button>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleNavigate(item.href)}
                        className="w-full flex items-center py-4 text-gray-900 dark:text-white hover:text-gray-600 dark:hover:text-white/80 transition-colors border-b border-gray-300/50 dark:border-white/10"
                      >
                        <span className="text-2xl font-light tracking-wide">
                          {t.nav[item.key as keyof typeof t.nav]}
                        </span>
                      </button>
                    )}
                  </motion.div>
                ))}
              </motion.div>

              {/* Language & Theme Settings */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.35 }}
                className="mt-6"
              >
                <div className="flex items-center justify-between">
                  {/* Language Selection */}
                  <div className="flex flex-wrap gap-3">
                    {locales.map((loc) => (
                      <button
                        key={loc.code}
                        onClick={() => {
                          handleLanguageChange(loc.code as Locale);
                          setIsMenuOpen(false);
                        }}
                          className={cn(
                            "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                            currentLocale === loc.code 
                              ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900" 
                              : "text-gray-600 dark:text-white/70 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-white/20 hover:border-gray-500 dark:hover:border-white/40"
                          )}
                      >
                        {loc.code.toUpperCase()}
                      </button>
                    ))}
                  </div>
                  
                  {/* Theme Toggle */}
                  <ThemeToggle size="md" />
                </div>
              </motion.div>

              {/* Bottom section with company info */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.4 }}
                className="mt-8 pt-8 border-t border-gray-300/50 dark:border-white/10"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-gray-500 dark:text-white/60 text-sm">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white mb-2">W. Althaus AG</p>
                    <p>Jurastrasse 12</p>
                    <p>CH-4912 Aarwangen</p>
                    <p className="mt-2">+41 62 919 82 10</p>
                    <p><EmailLink mailKey="info" className="hover:underline" /></p>
                  </div>
                  <div className="md:text-right">
                    <p className="font-medium text-gray-900 dark:text-white mb-2">Öffnungszeiten</p>
                    <p>Mo-Do: 07.30-12.00 / 13.00-16.00</p>
                    <p>Fr: 07.30-12.00 / 13.00-15.00</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
